import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { userActivityService } from '@/services/userActivity.service';
import type { UserActivityResponse } from '@/services/userActivity.service';
import { ROUTES } from '@/config/constant';

/**
 * Maximum days allowed for date range selection (90 days rolling window)
 */
const MAX_DATE_RANGE_DAYS = 90;

/**
 * Minimum hours to query when date range is invalid
 */
const MIN_HOURS = 24;

/**
 * Calculates the difference in hours between two dates
 * @param startDate - Start date string in ISO format (YYYY-MM-DD)
 * @param endDate - End date string in ISO format (YYYY-MM-DD)
 * @returns Number of hours between the dates, minimum 24 hours
 */
const calculateHoursDifference = (startDate: string, endDate: string): number => {
  const startDateTime = new Date(startDate).getTime();
  const endDateTime = new Date(endDate).getTime();
  const hoursDiff = Math.round((endDateTime - startDateTime) / (1000 * 60 * 60));
  return Math.max(hoursDiff, MIN_HOURS);
};

/**
 * Gets today's date in ISO format (YYYY-MM-DD)
 */
const getTodayString = (): string => new Date().toISOString().split('T')[0];

/**
 * Calculates the minimum allowed date (90 days ago from today)
 */
const getMinDateString = (): string => {
  const minDate = new Date();
  minDate.setDate(minDate.getDate() - MAX_DATE_RANGE_DAYS);
  return minDate.toISOString().split('T')[0];
};

/**
 * Custom hook for User Activity Dashboard
 * Manages data fetching, date range selection, and UI state
 * Follows the same pattern as useUserAccess for consistency
 */
export const useUserActivityDashboard = () => {
  const navigate = useNavigate();

  // ─── Core Data State ────────────────────────────────────────────────────────
  const [data, setData] = useState<UserActivityResponse | null>(null);

  // ─── UI State ───────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // ─── Date Range State ───────────────────────────────────────────────────────
  const todayStr = useMemo(() => getTodayString(), []);
  const [startDate, setStartDate] = useState<string>(todayStr);
  const [endDate, setEndDate] = useState<string>(todayStr);

  // ─── Date Constraints (Memoized) ────────────────────────────────────────────
  const today = useMemo(() => getTodayString(), []);
  const minDateStr = useMemo(() => getMinDateString(), []);

  // ─── Core Data Operations ───────────────────────────────────────────────────

  /**
   * Fetch user activity data from the API
   * Wrapped in useCallback for stable reference in useEffect
   */
  const fetchData = useCallback(async (start: string, end: string) => {
    setLoading(true);
    setError(null);
    try {
      // Validate date inputs
      if (!start || !end) {
        throw new Error('Invalid date range: Start and end dates are required');
      }

      // Convert dates to hours for the API
      const hoursDiff = calculateHoursDifference(start, end);
      const result = await userActivityService.getUserActivity(hoursDiff);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to load user activity data. Please try again.';
      setError(errorMessage);
      console.error('Error fetching user activity data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Effects ────────────────────────────────────────────────────────────────

  /**
   * Load default one-day data only on mount
   * Note: Date changes don't trigger automatic refetch - users must click Refresh
   */
  useEffect(() => {
    fetchData(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // ─── Date Change Handlers ───────────────────────────────────────────────────

  const handleStartDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newStart = event.target.value;
      if (!newStart) return;

      setStartDate(newStart);
      // Reset end date if it's before new start date
      if (endDate < newStart) {
        setEndDate(newStart);
      }
    },
    [endDate],
  );

  const handleEndDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newEnd = event.target.value;
      if (!newEnd) return;

      // Only update if end is after or equal to start
      if (newEnd >= startDate) {
        setEndDate(newEnd);
      }
    },
    [startDate],
  );

  // ─── Action Handlers ────────────────────────────────────────────────────────

  const handleRefresh = useCallback(() => {
    fetchData(startDate, endDate);
  }, [fetchData, startDate, endDate]);

  // ─── Navigation ─────────────────────────────────────────────────────────────

  const handleBackToDashboard = useCallback(() => {
    navigate(ROUTES.HOME);
  }, [navigate]);

  // ─── Alert Dismissal ────────────────────────────────────────────────────────

  const clearError = useCallback(() => setError(null), []);

  // ─── Derived Data ───────────────────────────────────────────────────────────

  const widgets = useMemo(() => data?.widgets, [data?.widgets]);
  const metadata = useMemo(() => data?.metadata, [data?.metadata]);

  return {
    // Data
    data,
    widgets,
    metadata,

    // UI state
    loading,
    error,

    // Date range state
    startDate,
    endDate,
    today,
    minDateStr,

    // Handlers
    handleStartDateChange,
    handleEndDateChange,
    handleRefresh,
    handleBackToDashboard,
    clearError,
  };
};

export default useUserActivityDashboard;
